import * as React from 'react';
import { BreakpointSizeType } from '../../../../../theme/type/breakpoints-type';
import { cn } from '../../../../../utils/tailwind';

const labelMobileClass: Record<BreakpointSizeType, string> = {
    xs: 'max-xs:flex max-xs:mr-xsmall max-xs:text-neutral-gray max-xs:font-normal',
    vs: 'max-vs:flex max-vs:mr-xsmall max-vs:text-neutral-gray max-vs:font-normal',
    sm: 'max-sm:flex max-sm:mr-xsmall max-sm:text-neutral-gray max-sm:font-normal',
    md: 'max-md:flex max-md:mr-xsmall max-md:text-neutral-gray max-md:font-normal',
    lg: 'max-lg:flex max-lg:mr-xsmall max-lg:text-neutral-gray max-lg:font-normal',
    vl: 'max-vl:flex max-vl:mr-xsmall max-vl:text-neutral-gray max-vl:font-normal',
    xl: 'max-xl:flex max-xl:mr-xsmall max-xl:text-neutral-gray max-xl:font-normal',
    xxl: 'max-xxl:flex max-xxl:mr-xsmall max-xxl:text-neutral-gray max-xxl:font-normal',
    fullhd: 'max-fullhd:flex max-fullhd:mr-xsmall max-fullhd:text-neutral-gray max-fullhd:font-normal',
};

const valueMobileClass: Record<BreakpointSizeType, string> = {
    xs: 'max-xs:flex-1 max-xs:text-right',
    vs: 'max-vs:flex-1 max-vs:text-right',
    sm: 'max-sm:flex-1 max-sm:text-right',
    md: 'max-md:flex-1 max-md:text-right',
    lg: 'max-lg:flex-1 max-lg:text-right',
    vl: 'max-vl:flex-1 max-vl:text-right',
    xl: 'max-xl:flex-1 max-xl:text-right',
    xxl: 'max-xxl:flex-1 max-xxl:text-right',
    fullhd: 'max-fullhd:flex-1 max-fullhd:text-right',
};

const cellMobileClass: Record<BreakpointSizeType, string> = {
    xs: 'max-xs:flex max-xs:justify-between max-xs:border-t max-xs:border-solid max-xs:border-border max-xs:px-0 max-xs:py-vsmall max-xs:first:border-t-0 max-xs:empty:hidden',
    vs: 'max-vs:flex max-vs:justify-between max-vs:border-t max-vs:border-solid max-vs:border-border max-vs:px-0 max-vs:py-vsmall max-vs:first:border-t-0 max-vs:empty:hidden',
    sm: 'max-sm:flex max-sm:justify-between max-sm:border-t max-sm:border-solid max-sm:border-border max-sm:px-0 max-sm:py-vsmall max-sm:first:border-t-0 max-sm:empty:hidden',
    md: 'max-md:flex max-md:justify-between max-md:border-t max-md:border-solid max-md:border-border max-md:px-0 max-md:py-vsmall max-md:first:border-t-0 max-md:empty:hidden',
    lg: 'max-lg:flex max-lg:justify-between max-lg:border-t max-lg:border-solid max-lg:border-border max-lg:px-0 max-lg:py-vsmall max-lg:first:border-t-0 max-lg:empty:hidden',
    vl: 'max-vl:flex max-vl:justify-between max-vl:border-t max-vl:border-solid max-vl:border-border max-vl:px-0 max-vl:py-vsmall max-vl:first:border-t-0 max-vl:empty:hidden',
    xl: 'max-xl:flex max-xl:justify-between max-xl:border-t max-xl:border-solid max-xl:border-border max-xl:px-0 max-xl:py-vsmall max-xl:first:border-t-0 max-xl:empty:hidden',
    xxl: 'max-xxl:flex max-xxl:justify-between max-xxl:border-t max-xxl:border-solid max-xxl:border-border max-xxl:px-0 max-xxl:py-vsmall max-xxl:first:border-t-0 max-xxl:empty:hidden',
    fullhd: 'max-fullhd:flex max-fullhd:justify-between max-fullhd:border-t max-fullhd:border-solid max-fullhd:border-border max-fullhd:px-0 max-fullhd:py-vsmall max-fullhd:first:border-t-0 max-fullhd:empty:hidden',
};

export interface TableCellTdPropsInterface {
    children?: React.ReactNode;
    align?: 'left' | 'center' | 'right';
    width?: number | string;
    label?: React.ReactNode;
    sticky?: 'left' | 'right';
    mobileBreakUnder?: BreakpointSizeType;
    isScrolled?: boolean;
    isScrollable?: boolean;
    isScrollOnEnd?: boolean;
    locked?: boolean;
    leftGap?: boolean;
    colSpan?: number;
}

export const TableCellTd: React.FC<TableCellTdPropsInterface> = ({
    children,
    align = 'left',
    sticky,
    width,
    label,
    mobileBreakUnder,
    isScrolled,
    isScrollable,
    isScrollOnEnd,
    locked,
    leftGap = true,
    colSpan,
}: TableCellTdPropsInterface) => {
    const widthValue = typeof width === 'number' ? `${width}rem` : width;
    const contentClassName = cn(
        'w-full text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        mobileBreakUnder && valueMobileClass[mobileBreakUnder],
    );

    return (
        <td
            colSpan={colSpan}
            className={cn(
                'relative border-0 px-small py-small text-medium leading-medium text-neutral-black',
                !leftGap && 'pl-0',
                align === 'center' && 'text-center',
                align === 'right' && 'text-right',
                locked && 'select-none blur-[0.3rem]',
                sticky === 'left' && 'sticky left-0 z-[100] bg-body',
                sticky === 'right' && 'sticky right-0 z-[100] bg-body',
                mobileBreakUnder && cellMobileClass[mobileBreakUnder],
                isScrolled &&
                    sticky === 'left' &&
                    'after:pointer-events-none after:absolute after:bottom-[-1px] after:right-0 after:top-0 after:w-[3rem] after:translate-x-full after:shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.3)] after:content-[""]',
                (isScrolled || isScrollable) &&
                    sticky === 'right' &&
                    'before:pointer-events-none before:absolute before:bottom-[-1px] before:left-[-6rem] before:top-0 before:w-[3rem] before:translate-x-full before:shadow-[inset_-10px_0_8px_-8px_rgba(0,0,0,0.3)] before:content-[""]',
                isScrollOnEnd && sticky === 'right' && 'before:hidden',
            )}
            style={widthValue ? ({ width: widthValue, minWidth: widthValue } as React.CSSProperties) : undefined}
        >
            {label && (
                <div className={cn('hidden', mobileBreakUnder && labelMobileClass[mobileBreakUnder])}>{label}:</div>
            )}
            {children && <div className={contentClassName}>{children}</div>}
        </td>
    );
};
